// @flow

import React, { type Node, useMemo, useEffect } from 'react';
import { type AppValues } from '@tectonics/app-values';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { getCountry, type IncomingPayment } from '@tectonics/prs/incoming-payment';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { getUserInputValidator } from '@tectonics/prs/validation';
import { type LookAndFeel, type LookAndFeelRoot, type LookAndFeelLoadingPage } from '@tectonics/look-and-feel';
import { useCustomReceiptsHandler } from './use-custom-receipts-handler';
import { useHandleUserInput } from './use-handle-user-input';
import { useSelectedPaymentMethod } from './use-selected-payment-method';
import { useSubmit } from './use-submit';
import { useSubmitError } from './use-submit-error';
import { useUserInputValidationErrors } from './use-user-input-validation-errors';
import { useLookAndFeelAnalyzer } from './use-look-and-feel-analyzer';
import { LOOK_AND_FEELS } from './look-and-feels';
import { LafChooser } from './laf-chooser';
import { LookAndFeelRender } from './look-and-feel-render';
import { initAnalytics } from './analytics';

type PaymentRenderProps = {|
  +incomingPayment: IncomingPayment,
  +setIncomingPayment: (IncomingPayment) => void,
  +spec: PaymentSpec,
  +lookAndFeel: LookAndFeel,
  +setLookAndFeel: (LookAndFeel) => void,
  +lookAndFeelRootPage: LookAndFeelRoot,
  +lookAndFeelLoadingPage: ?LookAndFeelLoadingPage,
  +appValues: AppValues,
  +setAppValues: (AppValues) => void,
  +userInputValues: UserInputValues,
  +setUserInputValues: (UserInputValues) => void,
  +requestReloadContext: () => void,
|};

export function PaymentRender({
  incomingPayment,
  setIncomingPayment,
  spec,
  lookAndFeel,
  setLookAndFeel,
  lookAndFeelLoadingPage: LookAndFeelLoadingPage,
  lookAndFeelRootPage,
  appValues,
  setAppValues,
  userInputValues,
  setUserInputValues,
  requestReloadContext,
}: PaymentRenderProps): Node {

  const prsSupport = lookAndFeel.prsSupport;
  const selectedPaymentMethodCode = userInputValues['selectedPaymentMethod'];
  const validateUserInput = useMemo(() => getUserInputValidator(selectedPaymentMethodCode, getCountry(incomingPayment)) , [selectedPaymentMethodCode, incomingPayment]);
  const handleUserInput = useHandleUserInput(userInputValues, setUserInputValues);
  const [userInputValidationErrors, setUserInputValidationErrors, isFirstTimeValidating] = useUserInputValidationErrors({ spec, prsSupport, userInputValues, validateUserInput, incomingPayment });
  const [selectedPaymentMethod, setSelectedPaymentMethodByCode] = useSelectedPaymentMethod(spec);
  const [submitError, setSubmitError] = useSubmitError();

  const analytics = useMemo(() =>
    initAnalytics({incomingPayment})
  , [incomingPayment]);

  useEffect(() => {
    analytics.setUserInputValues(userInputValues);
  }, [analytics, userInputValues]);

  useEffect(() => {
    analytics.setSubmitError(submitError);
  }, [analytics, submitError]);

  useLookAndFeelAnalyzer(lookAndFeel, spec);

  const [submitting, handleSubmit] = useSubmit({
    lookAndFeel,
    spec,
    prsSupport,
    incomingPayment,
    setIncomingPayment,
    setSubmitError,
    setUserInputValidationErrors,
    appValues,
    userInputValues,
    validateUserInput,
  });

  const customReceiptsHandler = useCustomReceiptsHandler(incomingPayment, setIncomingPayment, userInputValues);

  if (isFirstTimeValidating) {
    if (LookAndFeelLoadingPage)
      return <LookAndFeelLoadingPage />;
    else
      return null;
  }

  return (
    <>
      <LookAndFeelRender
        root={lookAndFeelRootPage}
        spec={spec}
        incomingPayment={incomingPayment}
        appValues={appValues}
        setAppValues={setAppValues}
        onUserInput={handleUserInput}
        userInputValues={userInputValues}
        userInputValidationErrors={userInputValidationErrors}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethodByCode={setSelectedPaymentMethodByCode}
        submitting={submitting}
        handleSubmit={handleSubmit}
        submitError={submitError}
        analytics={analytics}
        customReceiptsHandler={customReceiptsHandler}
        requestReloadContext={requestReloadContext}
      />

      {ENV_CONSTANTS.ENV === 'development' && (
        <LafChooser
          currentLookAndFeel={lookAndFeel}
          setCurrentLookAndFeel={setLookAndFeel}
          availableLookAndFeels={LOOK_AND_FEELS}
        />
      )}
    </>
  );
}
